<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { computedAsync } from '@vueuse/core';
import adminAPI from '@/services/api';
import { useNotificationStore } from '@/store';
import { openBoxFile } from "@/utils/open-box-folder";

import AlertModal from '../ui/modals/AlertModal.vue';
import { Company, CompanyInput, UploadFileSchema } from '@upclaim/sdk/src/data-contracts';
import BaseInput from '../ui/inputs/BaseInput.vue';
import { ClipboardEditIcon } from 'lucide-vue-next';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronUpIcon, CloudArrowUpIcon } from '@heroicons/vue/20/solid'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline';
import FileInput from '../files/FileInput.vue';
import Alert from '../ui/alerts/Alert.vue';
import BaseButton from '../ui/buttons/BaseButton.vue';
import SelectCourt from '../ui/select/SelectCourt.vue';


const props = defineProps<{
  companyId?: any
  company?: Company,
  relation?: { travelAgencyId: string } | { travelAgencyCompanyId: string } | { airlineId: string }
}>();
const emit = defineEmits(["close"]);

const open = ref(false);
const isLoading = ref(false);
const companyDetail = computedAsync(async () => {
  return props.companyId
    ? adminAPI.retrieveCompany(props.companyId)
    : props.company
}, null, isLoading);
const isNewCompany = computed(() => !isLoading.value && !companyDetail.value?.id);

const companyInput = ref<CompanyInput>({});
watch(companyDetail, (newValue) => {
  companyInput.value = {
    siren: newValue?.siren,
    siret: newValue?.siret,
    euVAT: newValue?.euVAT,
    legalNotices: newValue?.legalNotices
  }
})

async function upsertCompany() {
  if (!companyInput.value) return;
  companyDetail.value = await adminAPI.upsertCompany({...companyInput.value, ...(props.relation || {}) });
  useNotificationStore().emitNotification({ title: "Fiche entreprise enregistrée avec succès" });
  if (kbisInput.value) await uploadKbis();
  open.value = false;
}

const kbisInput = ref<UploadFileSchema>();
async function uploadKbis() {
  if (!kbisInput.value) {
    useNotificationStore().emitNotification({ title: "Aucun fichier détecté, veuillez ré-essayer" })
    return;
  }
  if (!companyDetail?.value?.id) {
    useNotificationStore().emitNotification({ title: "Veuillez d'abord initialiser la fiche entreprise, puis recommencer" })
    return;
  }
  const waitNotification = useNotificationStore().emitNotification({ title: "Envoie en cours...", type: "wait" });
  try {
    await adminAPI.uploadCompanyKbis(companyDetail.value.id, kbisInput.value);
    companyDetail.value = await adminAPI.retrieveCompany(props.companyId);
    useNotificationStore().emitNotification({ title: "KBIS enregistré avec succès", type: "success" });
  } catch (error) {}
  finally {
    waitNotification.duration = 0;
  }
}
</script>
<template>
<AlertModal type="info"
  :open="open"
  :title="isNewCompany ? 'Créer une nouvelle fiche entreprise' : 'Fiche entreprise'"
  message=""
  confirm-message="Enregistrer"
  cancel-message="Fermer"
  @confirm="() => upsertCompany()"
  @close="open = false"
  @cancel="open = false"
  width="max-w-xl"
>
  <div class="py-6">
    
    <div class="w-full">
    <div class="w-full p-2 mx-auto bg-white rounded-2xl">
      <Disclosure :default-open="true" v-slot="{ open }">
        <DisclosureButton
          class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-purple-900 bg-purple-100 rounded-lg hover:bg-purple-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500/75"
        >
          <span>Informations Juridiques</span>
          <ChevronUpIcon
            :class="open ? 'rotate-180 transform' : ''"
            class="w-5 h-5 text-purple-500"
          />
        </DisclosureButton>
        <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">

          <form class="divide-y-8 divide-transparent ">
            <BaseInput label="Nom de la société" @update:model-value="(value: any) => companyInput.name = value" :value="companyDetail?.name" />
            <div class="grid grid-cols-2 mb-2 divide-x-8 divide-transparent">
              <BaseInput label="SIREN" placeholder="Exemple : 890120587" tooltip="Utilisé pour synchroniser les données Pappers" @update:model-value="(value: any) => companyInput.siren = value" :value="companyDetail?.siren" />
              <BaseInput label="SIRET" placeholder="Exemple : 89012058700021" @update:model-value="(value: any) => companyInput.siret = value" :value="companyDetail?.siret" />
            </div>
            <div class="mb-2">
              <BaseInput label="Numéro TVA Européenne" description="Si société étrangère" placeholder="Exemple : ATU72316013" @update:model-value="(value: any) => companyInput.euVAT = value" :value="companyDetail?.euVAT" />
            </div>

            <SelectCourt @update:model-value="({value}) => companyInput.competentCourtId = value" :selectedCourtId="companyDetail?.competentCourtId" v-if="!isLoading" v-permission="['UPCLAIM_ADMIN', 'LAWYER']" />
          </form>

        </DisclosurePanel>
      </Disclosure>


      <Disclosure :default-open="true" v-slot="{ open }">
        <DisclosureButton
          class="flex justify-between w-full px-4 py-2 mt-6 text-sm font-medium text-left text-purple-900 bg-purple-100 rounded-lg hover:bg-purple-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500/75"
        >
          <span>Adresse Postale / Siège</span>
          <ChevronUpIcon
            :class="open ? 'rotate-180 transform' : ''"
            class="w-5 h-5 text-purple-500"
          />
        </DisclosureButton>
        <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">

          <form class="divide-y-8 divide-transparent ">
            <BaseInput label="Description de l'adresse" placeholder="Exemple : Siège de la société "
              @update:model-value="(value: any) => {
                if (!companyInput.postalAddress) companyInput.postalAddress = {} as any; // @ts-ignore
                else companyInput.postalAddress.label = value;
              }"
              :value="companyDetail?.postalAddress?.label"
            />
            <BaseInput label="Destinataire" placeholder="Exemple : Air France"
              @update:model-value="(value: any) => {
                if (!companyInput.postalAddress) companyInput.postalAddress = {} as any; // @ts-ignore
                else companyInput.postalAddress.nameLine = value;
              }"
              :value="companyDetail?.postalAddress?.nameLine"
            />
            <BaseInput label="Adresse" placeholder="Exemple : 15 rue Simone Veil"
              @update:model-value="(value: any) => {
                if (!companyInput.postalAddress) companyInput.postalAddress = {} as any; // @ts-ignore
                else companyInput.postalAddress.streetAddress = value;
              }"
              :value="companyDetail?.postalAddress?.streetAddress"
            />
            <BaseInput label="Complément adresse" placeholder="Exemple : Immeuble Atlantis"
              @update:model-value="(value: any) => {
                if (!companyInput.postalAddress) companyInput.postalAddress = {} as any; // @ts-ignore
                else companyInput.postalAddress.streetAddressComplement = value;
              }"
              :value="companyDetail?.postalAddress?.streetAddressComplement"
            />
            <div class="grid grid-cols-3 divide-x-8 divide-transparent">
              <BaseInput label="Code postal" placeholder="Ex : 94000" :value="companyDetail?.postalAddress?.zipCode"
                @update:model-value="(value: any) => {
                  if (!companyInput.postalAddress) companyInput.postalAddress = {} as any; // @ts-ignore
                  else companyInput.postalAddress.zipCode = value;
                }"
              />
              <BaseInput label="Ville" placeholder="Ex : Paris" :value="companyDetail?.postalAddress?.city"
                @update:model-value="(value: any) => {
                  if (!companyInput.postalAddress) companyInput.postalAddress = {} as any; // @ts-ignore
                  else companyInput.postalAddress.city = value;
                }"
              />
              <BaseInput label="Pays" placeholder="FR" :value="companyDetail?.postalAddress?.countryCode"
                tooltip="Code ISO à 2 lettres"
                @update:model-value="(value: any) => {
                  if (!companyInput.postalAddress) companyInput.postalAddress = {} as any; // @ts-ignore
                  else companyInput.postalAddress.countryCode = value;
                }"
              />
            </div>
          </form>

        </DisclosurePanel>
      </Disclosure>


      <Disclosure :default-open="true" as="div" class="mt-8" v-slot="{ open }">
        <DisclosureButton
          class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-purple-900 bg-purple-100 rounded-lg hover:bg-purple-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500/75"
        >
          <span>Mentions Légales</span>
          <ChevronUpIcon
            :class="open ? 'rotate-180 transform' : ''"
            class="w-5 h-5 text-purple-500"
          />
        </DisclosureButton>
        <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
          <label for="legal-notices" class="block text-sm font-medium leading-6 text-gray-900">Mentions légales de la société :</label>
          <div class="mt-2">
            <textarea :rows="4" name="legal-notices" id="legal-notices" v-model="companyInput.legalNotices"
              class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" />
          </div>
        </DisclosurePanel>
      </Disclosure>


      <Disclosure :default-open="true" as="div" class="mt-8" v-slot="{ open }">
        <DisclosureButton
          class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-purple-900 bg-purple-100 rounded-lg hover:bg-purple-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500/75"
        >
          <span>KBIS</span>
          <ChevronUpIcon
            :class="open ? 'rotate-180 transform' : ''"
            class="w-5 h-5 text-purple-500"
          />
        </DisclosureButton>
        <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
          <div v-if="!companyDetail?.kbisBoxFileId">
            <p class="px-2 italic">Aucun KBIS renseigné pour l'instant</p>
            <FileInput @files-updated="(files) => kbisInput = { name: files[0].file.name, document: files[0].content }"  />
          </div>
          <div v-else>
            <Alert title="Un KBIS est enregistré pour cette société" type="success" :withBorder="false">
              <div class="mt-6">
                <BaseButton text="Voir sur Box" :icon="ArrowTopRightOnSquareIcon" @click="() => openBoxFile(companyDetail?.kbisBoxFileId as string)" />
              </div>
            </Alert>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  </div>

  </div>

</AlertModal>

<div @click="open = !open">
  <slot />
</div>
</template>